export interface DependentInfo {
  name: string;
  relationship: string;
  dateOfBirth: Date | null;
  nationality: string;
  passportNumber: string;
}

export interface SponsorInfo {
  name: string;
  relationship: string;
  fundingPercentage: number;
  visaStatus: string;
  location: string;
  inAustralia: boolean;
}

export interface GenuineStudentFormValues {
  applicationId?: string;

  // Home Country Ties
  hasImmediateFamilyHome: boolean | null;
  immediateFamilyMembers: string[];
  hasDependents: boolean | null;
  dependentTypes: string[];
  hasCommunityInvolvement: boolean | null;
  ownsProperty: boolean | null;
  ownsBusiness: boolean | null;
  hasFamilyInHost: boolean | null;
  familyInHostRelationship: string;
  communityOrganizationTypes: string[];
  otherCommunityOrganization: string;
  communityIntegration: string;
  familyCommunityTies: string;
  bringingDependents: boolean | null;
  numberOfDependents: number | null;
  dependents: string[];
  understandDependentWorkRights: boolean | null;
  dependentHealthInsuranceArranged: boolean | null;
  currentlyEmployed: boolean | null;
  employmentType: string;
  yearsInPosition: number | null;
  industry: string;
  positionHeld: boolean | null;
  mainIncomeSource: string;
  otherMainIncomeSource: string;
  propertyTypes: string[];
  propertyValuationAmount: number | null;
  propertyValuationCurrency: string;
  businessRole: string;
  hasInvestments: boolean | null;
  employmentEconomicSituation: string;

  // Course & Career
  totalWorkExperience: number | null;
  workRelatedToStudy: boolean | null;
  employerSupportsStudy: boolean | null;
  employmentHistoryRelevance: string;
  courseAvailableHome: boolean | null;
  whyStudyAbroadReasons: string[];
  otherWhyStudyAbroadReasons: string;
  courseRelatedPrevEducation: boolean | null;
  courseRequiredLicensing: boolean | null;
  researchedAlternatives: boolean | null;
  researchActivities: string[];
  consideredOtherCountries: boolean | null;
  countriesConsidered: string;
  whyThisCourse: string;
  whyThisProvider: string;
  whyThisCountry: string;
  postGraduationWorkLocation: string;
  hasSpecificCareerGoal: boolean | null;
  targetRole: string;
  qualificationDemand: string;
  expectedIncomeChange: string;
  qualificationUsePlans: string[];
  otherQualificationUsePlans: string;
  hasJobOffer: boolean | null;
  returnToFamilyBusiness: boolean | null;
  careerBenefitsPlans: string;
  hasRelevantTraining: boolean | null;
  trainingDetails: string;

  // Immigration & Intent
  traveledInternationally: boolean | null;
  countriesVisited: number | null;
  alwaysCompliedVisa: boolean | null;
  expectedProgramDuration: number | null;
  planFurtherStudy: boolean | null;
  furtherStudyType: string;
  seekWorkAuthorization: boolean | null;
  intendedWorkDuration: string;
  interestedPermanentResidence: boolean | null;
  permanentResidenceLevel: string;
  planReturnHome: string;
  everHadVisaRefused: boolean | null;
  visaRefusalCount: number | null;
  mostRecentRefusalCountry: string;
  mostRecentRefusalYear: Date | null;
  everHadVisaCancelled: boolean | null;
  everOverstayedVisa: boolean | null;
  everDeported: boolean | null;
  everBannedEntry: boolean | null;
  everWorkedWithoutAuth: boolean | null;
  familyMemberVisaIssues: boolean | null;
  visaHistoryExplanation: string;
  studiedInHostBefore: boolean | null;
  completedPreviousProgram: boolean | null;
  previousProgramNonCompletionReason: string;
  otherPreviousProgramNonCompletionReason: string;
  previousStudyHistory: string;
  currentlyInHostCountry: boolean | null;
  currentVisaType: string;
  currentVisaExpiry: Date | null;
  changingVisaType: boolean | null;
  visaChangeRationale: string;

  // Financial & Preparation
  primaryFundingSource: string;
  otherPrimaryFundingSource: string;
  fundingFullySecured: boolean | null;
  totalFundsAvailable: number | null;
  totalFundsCurrency: string;
  scholarshipLoanApproved: boolean | null;
  hasIncomeGeneratingAssets: boolean | null;
  hasFinancialSponsors: boolean | null;
  sponsor1FamilyName?: string;
  sponsor1GivenName?: string;
  sponsor1Country?: string;
  sponsor1Relationship?: string;
  sponsor1Occupation?: string;
  sponsor1Income?: number;
  sponsor1Currency?: string;
  sponsor2FamilyName?: string;
  sponsor2GivenName?: string;
  sponsor2Country?: string;
  sponsor2Relationship?: string;
  sponsor2Occupation?: string;
  sponsor2Income?: number;
  sponsor2Currency?: string;
  sponsor3FamilyName?: string;
  sponsor3GivenName?: string;
  sponsor3Country?: string;
  sponsor3Relationship?: string;
  sponsor3Occupation?: string;
  sponsor3Income?: number;
  sponsor3Currency?: string;
  sponsor4FamilyName?: string;
  sponsor4GivenName?: string;
  sponsor4Country?: string;
  sponsor4Relationship?: string;
  sponsor4Occupation?: string;
  sponsor4Income?: number;
  sponsor4Currency?: string;
  hasFinancialObligationsHome: boolean | null;
  financialObligationTypes: string[];
  incomeGeneratingAssetTypes: string[];
  otherIncomeGeneratingAssetTypes: string;
  financialCapacity: string;
  researchConductedActivities: string[];
  researchedLivingCosts: boolean | null;
  estimatedMonthlyExpenses: number | null;
  estimatedExpensesCurrency: string;
  arrangedAccommodation: boolean | null;
  accommodationType: string;
  understandingCourseRequirements: string;
  understandingLivingCosts: string;
  understandingVisaConditions: string;
  understandingWorkRestrictions: string;
  studyLivingKnowledge: string;
  englishTestTaken: string;
  englishTestScore: number | null;
  englishTestDate: Date | null;
  hasEducationEmploymentGaps: boolean | null;
  gapPrimaryActivity: string;
  otherGapPrimaryActivity: string;
  gapPeriodExplanation: string;
  applicationPreparation: string[];
  healthInsuranceArranged: boolean | null;
  healthInsuranceProvider: string;
  otherHealthInsuranceProvider: string;
  healthInsuranceDuration: string;

  sopGuidelinesAcknowledged: boolean | null;
  sopRequirementsUnderstood: boolean | null;

  declarations?: {
    mandatory: Array<{
      id: string;
      text: string;
      isAccepted: boolean;
    }>;
  };
  checklist?: {
    completedItems: Array<{
      id: string;
      label: string;
      isCompleted: boolean;
    }>;
    requiredItems: Array<{
      id: string;
      label: string;
      isCompleted: boolean;
    }>;
    isComplete: boolean;
    completionPercentage: number;
  };
}

export const genuineStudentDocumentTypes = [
  "MARRIAGE_CERTIFICATE",
  "BIRTH_CERTIFICATE",
  "FAMILY_REGISTER",
  "COMMUNITY_MEMBERSHIP_CERTIFCATE",
  "COMMUNITY_LETTER_OF_APPOINTMENT",
  "DEPENDENT_PASSPORT",
  "JOINT_BANK_STATEMENTS",
  "JOINT_LEASE",
  "JOINT_UTILITY_BILLS",
  "RELATIONSHIP_PHOTOS",
  "STATUTORY_DECLARATIONS",
  "DEPENDENT_BIRTH_CERTIFICATE",
  "DEPENDENT_ACADEMICS",
  "DEPENDENT_EMPLOYMENT",
  "EMPLOYMENT_LETTER",
  "PAYSLIPS",
  "INCOME_TAX",
  "PROPERTY_DEED",
  "PROPERTY_TAX_DOCUMENT",
  "PROPERTY_VALUATION",
  "LEASE_AGREEMENT",
  "BUSINESS_REGISTRATION_CERTIFICATE",
  "RESUME",
  "EMPLOYMENT_1_REFERENCE",
  "HIGH_SCHOOL_TRANSCRIPT",
  "TERTIARY_TRANSCRIPT_1",
  "TERTIARY_TRANSCRIPT_2",
  "TERTIARY_TRANSCRIPT_3",
  "PROVIDER_CORRESPONDENCE",
  "EMPLOYER_CORRESPONDENCE",
  "VISA_REFUSAL_LETTERS",
  "VISA_CANCELLATION_NOTICE",
  "PREVIOUS_ENROLLMENT_LETTER",
  "PREVIOUS_PROGRAM_TRANSCRIPT",
  "CURRENT_VISA",
  "BANK_STATEMENTS",
  "LOAN_DOCUMENTS",
  "SCHOLARSHIP_DOCUMENTS",
  "SPONSOR_IDENTITY",
  "RELATIONSHIP_EVIDENCE",
  "SPONSOR_LETTER",
  "SPONSOR_CITIZENSHIP",
  "SPONSOR_VISA",
  "SPONSOR_INCOME_TAX",
  "TRAINING_CERTIFICATES",
  "OSHC_RECEIPT",
  "DEPENDENT_OSHC",
  "STATEMENT_OF_PURPOSE",
];
